import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BotCheckWidget } from '@byscott-io/corebyscott';
import { publicRecipes } from '../api/client';

// Anonymous public recipe submission (#520). Visitor doesn't need an
// account — fills in a recipe, completes the bot check, lands in the
// maintainer's review queue. After approval the recipe appears in the
// public catalog.
export default function ContributeRecipePage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: '',
    description: '',
    instructions: '',
    servings: '',
    prep_time_minutes: '',
    cook_time_minutes: '',
    source_url: '',
    notes: '',
    submitter_name: '',
    submitter_email: '',
  });
  const [ingredients, setIngredients] = useState([{ name: '', quantity: '', unit: '' }]);
  const [botFields, setBotFields] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setIng = (idx, key, value) => {
    setIngredients((prev) => prev.map((row, i) => (i === idx ? { ...row, [key]: value } : row)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);

    const cleanedIngredients = ingredients
      .map((i) => ({ ...i, name: i.name.trim() }))
      .filter((i) => i.name.length > 0);

    const payload = {
      recipe: {
        ...form,
        servings: form.servings ? Number(form.servings) : null,
        prep_time_minutes: form.prep_time_minutes ? Number(form.prep_time_minutes) : null,
        cook_time_minutes: form.cook_time_minutes ? Number(form.cook_time_minutes) : null,
        ingredients_attributes: cleanedIngredients,
      },
      submitter_name: form.submitter_name,
      submitter_email: form.submitter_email,
      ...botFields,
    };

    try {
      await publicRecipes.submit(payload);
      setSuccess(true);
    } catch (err) {
      const apiErr = err.response?.data;
      setError(apiErr?.error || (Array.isArray(apiErr?.errors) ? apiErr.errors.join(', ') : err.message));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <h1 className="text-3xl font-bold mb-4">🎉 Thanks for contributing!</h1>
        <p className="text-gray-700 mb-6">
          Your recipe is now live in the public catalog.
        </p>
        <div className="flex gap-3 justify-center">
          <Link to="/" className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700">
            Browse public recipes
          </Link>
          <button
            type="button"
            onClick={() => { setSuccess(false); setForm({ ...form, title: '', description: '', instructions: '' }); setIngredients([{ name: '', quantity: '', unit: '' }]); setBotFields({}); navigate(0); }}
            className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
          >
            Submit another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <Link to="/" className="text-indigo-600 hover:text-indigo-800 text-sm">&larr; Back</Link>
      <h1 className="text-3xl font-bold mt-2 mb-2">Contribute a recipe</h1>
      <p className="text-gray-600 mb-6">
        No account needed. Your submission will be reviewed by a maintainer before going live.
      </p>

      <form onSubmit={handleSubmit} className="space-y-5">
        <Field label="Recipe title *">
          <input
            type="text"
            required
            value={form.title}
            onChange={(e) => setField('title', e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          />
        </Field>

        <Field label="Short description">
          <textarea
            rows={2}
            value={form.description}
            onChange={(e) => setField('description', e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          />
        </Field>

        <Field label="Ingredients *">
          <div className="space-y-2">
            {ingredients.map((row, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2">
                <input
                  type="text"
                  placeholder="qty"
                  value={row.quantity}
                  onChange={(e) => setIng(idx, 'quantity', e.target.value)}
                  className="col-span-2 px-2 py-1 border border-gray-300 rounded-md text-sm"
                />
                <input
                  type="text"
                  placeholder="unit"
                  value={row.unit}
                  onChange={(e) => setIng(idx, 'unit', e.target.value)}
                  className="col-span-2 px-2 py-1 border border-gray-300 rounded-md text-sm"
                />
                <input
                  type="text"
                  placeholder="ingredient"
                  value={row.name}
                  onChange={(e) => setIng(idx, 'name', e.target.value)}
                  className="col-span-7 px-2 py-1 border border-gray-300 rounded-md text-sm"
                />
                <button
                  type="button"
                  onClick={() => setIngredients((prev) => prev.filter((_, i) => i !== idx))}
                  className="col-span-1 text-red-600 text-sm"
                  aria-label="Remove ingredient"
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setIngredients((prev) => [...prev, { name: '', quantity: '', unit: '' }])}
              className="text-sm text-indigo-600 hover:text-indigo-800"
            >
              + Add ingredient
            </button>
          </div>
        </Field>

        <Field label="Instructions *">
          <textarea
            rows={6}
            required
            value={form.instructions}
            onChange={(e) => setField('instructions', e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          />
        </Field>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Servings"><NumInput value={form.servings} onChange={(v) => setField('servings', v)} /></Field>
          <Field label="Prep (min)"><NumInput value={form.prep_time_minutes} onChange={(v) => setField('prep_time_minutes', v)} /></Field>
          <Field label="Cook (min)"><NumInput value={form.cook_time_minutes} onChange={(v) => setField('cook_time_minutes', v)} /></Field>
        </div>

        <Field label="Source URL (optional)">
          <input
            type="url"
            value={form.source_url}
            onChange={(e) => setField('source_url', e.target.value)}
            placeholder="https://example.com/recipe"
            className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          />
        </Field>

        <div className="border-t border-gray-200 pt-5">
          <h2 className="font-semibold mb-3 text-gray-900">Credit (optional)</h2>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Your name">
              <input
                type="text"
                value={form.submitter_name}
                onChange={(e) => setField('submitter_name', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </Field>
            <Field label="Email (we'll only use this if we have a question)">
              <input
                type="email"
                value={form.submitter_email}
                onChange={(e) => setField('submitter_email', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </Field>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-5">
          <BotCheckWidget
            onToken={setBotFields}
            challengeUrl={publicRecipes.botCheckChallengeUrl}
          />
        </div>

        {error && (
          <div className="text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2 text-sm">{error}</div>
        )}

        <div className="flex justify-end gap-2">
          <Link to="/" className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50">Cancel</Link>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
          >
            {submitting ? 'Submitting…' : 'Submit for review'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-gray-700 mb-1">{label}</span>
      {children}
    </label>
  );
}

function NumInput({ value, onChange }) {
  return (
    <input
      type="number"
      min="0"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
    />
  );
}
